import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { UPDATE_HHG_ESTIMATE_FAILURE } from 'modules/authorizations/store/types/houseHoldGoodsTypes';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import { getHhgSegments, updateHhgEstimate } from 'modules/authorizations/store/actions/houseHoldGoods.actions';
import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  Fab,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { makeStyles } from '@material-ui/styles';
import Spinner from 'modules/common/spinner.component';
import { formatMoney } from 'utilities/currencyFormatter';

const useStyles = makeStyles(theme => ({
  card: {
    width: '100%',
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardContainer: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  iconRow: {
    display: 'flex',
    flexDirection: 'row'
  },
  dialogIconRow: {
    display: 'flex',
    flexDirection: 'row',
    padding: '40px',
    paddingBottom: '20px'
  },
  cardDisplayContent: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    margin: '10px'
  },
  chip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  chipClickable: {
    cursor: 'pointer'
  },
  iconOverlayImg: {
    position: 'absolute',
    zIndex: '1 !important'
  },
  spacingX: {
    marginRight: theme.spacing(1)
  },
  footer: {
    height: 15,
    marginBottom: theme.spacing(2)
  },
  mb2: {
    marginBottom: '4px',
    marginRight: '16px'
  },
  editModalContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100% !important',
    marginLeft: '0px !important'
  },
  editModalColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100% !important'
  },
  editModalInput: {
    minWidth: '332px'
  },
  dialogContentContainer: {
    margin: '15px',
    paddingLeft: '50px',
    paddingRight: '50px'
  },
  labelText: {
    minWidth: 350,
    marginBottom: 0
  }
}));

const EstimateInputRow = ({ label, value, actualValue, onChange, onActualChange, unit = '$' }) => (
  <>
    <Grid item md={3}>
      <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
        {label}
      </Typography>
    </Grid>

    <Grid item md={3}>
      <TextField
        fullWidth
        value={value ?? 0}
        margin="dense"
        variant="standard"
        type="number"
        InputProps={{
          startAdornment: <InputAdornment position="start">{unit}</InputAdornment>
        }}
        onChange={e => {
          onChange(e.target.value);
        }}
      />
    </Grid>

    <Grid item md={3}>
      <TextField
        fullWidth
        value={actualValue ?? 0}
        margin="dense"
        variant="standard"
        type="number"
        InputProps={{
          startAdornment: <InputAdornment position="start">{unit}</InputAdornment>
        }}
        onChange={e => {
          onActualChange(e.target.value);
        }}
      />
    </Grid>

    <Grid item md={3}></Grid>
  </>
);

const EstimateDisplayRow = ({ label, value, actualValue }) => (
  <>
    <Grid item md={3}>
      <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
        {label}
      </Typography>
    </Grid>

    <Grid item md={3}>
      <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
        {value}
      </Typography>
    </Grid>

    <Grid item md={3}>
      <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
        {actualValue}
      </Typography>
    </Grid>

    <Grid item md={3}></Grid>
  </>
);

const EditCardModal = props => {
  const { open, onSubmit, isLoading, onClose, estimateInfo } = props;
  const classes = useStyles();

  const [discountRate, setDiscountRate] = useState(estimateInfo ? estimateInfo.discountRate : 0);
  const [storageDiscountRate, setStorageDiscountRate] = useState(estimateInfo ? estimateInfo.storageDiscountRate : 0);
  const [landWeight, setLandWeight] = useState(estimateInfo ? estimateInfo.landWeight : 0);
  const [airWeight, setAirWeight] = useState(estimateInfo ? estimateInfo.airWeight : 0);
  const [seaWeight, setSeaWeight] = useState(estimateInfo ? estimateInfo.seaWeight : 0);
  const [storageRate, setStorageRate] = useState(estimateInfo ? estimateInfo.storageRate : 0);
  const [doorToDoorRate, setDoorToDoorRate] = useState(estimateInfo ? estimateInfo.doorToDoorRate : 0);
  const [extraTransitProtectionRate, setExtraTransitProtectionRate] = useState(estimateInfo ? estimateInfo.extraTransitProtectionRate : 0);
  const [thirdPartyEstimates, setThirdPartyEstimates] = useState(estimateInfo ? estimateInfo.thirdPartyEstimates : 0);
  const [hasInvoicesPaid, setHasInvoicesPaid] = useState(
    estimateInfo && estimateInfo.hasInvoicesPaid ? estimateInfo.hasInvoicesPaid : false
  );

  const [actualDiscountRate, setActualDiscountRate] = useState(estimateInfo?.actualDiscountRate ?? 0);
  const [actualStorageDiscountRate, setActualStorageDiscountRate] = useState(estimateInfo?.actualStorageDiscountRate ?? 0);
  const [actualLandWeight, setActualLandWeight] = useState(estimateInfo?.actualLandWeight ?? 0);
  const [actualAirWeight, setActualAirWeight] = useState(estimateInfo?.actualAirWeight ?? 0);
  const [actualSeaWeight, setActualSeaWeight] = useState(estimateInfo?.actualSeaWeight ?? 0);
  const [actualStorageRate, setActualStorageRate] = useState(estimateInfo?.actualStorageRate ?? 0);
  const [actualDoorToDoorRate, setActualDoorToDoorRate] = useState(estimateInfo?.actualDoorToDoorRate ?? 0);
  const [actualExtraTransitProtectionRate, setActualExtraTransitProtectionRate] = useState(
    estimateInfo?.actualExtraTransitProtectionRate ?? 0
  );
  const [actualThirdPartyEstimates, setActualThirdPartyEstimates] = useState(estimateInfo?.actualThirdPartyEstimates ?? 0);

  const [isDirty, setIsDirty] = useState(false);

  const onSubmitModal = e => {
    e.preventDefault();

    const values = {
      discountRate,
      storageDiscountRate,
      landWeight,
      airWeight,
      seaWeight,
      storageRate,
      doorToDoorRate,
      extraTransitProtectionRate,
      thirdPartyEstimates,

      actualDiscountRate,
      actualStorageDiscountRate,
      actualLandWeight,
      actualAirWeight,
      actualSeaWeight,
      actualStorageRate,
      actualDoorToDoorRate,
      actualExtraTransitProtectionRate,
      actualThirdPartyEstimates,

      estimateInfo,
      hasInvoicesPaid
    };

    onSubmit(values);
  };

  useEffect(() => {
    if (estimateInfo) {
      if (
        (!(discountRate === 0 && estimateInfo.discountRate == null) && discountRate !== estimateInfo.discountRate) ||
        (!(storageDiscountRate === 0 && estimateInfo.storageDiscountRate == null) &&
          storageDiscountRate !== estimateInfo.storageDiscountRate) ||
        (!(landWeight === 0 && estimateInfo.landWeight == null) && landWeight !== estimateInfo.landWeight) ||
        (!(airWeight === 0 && estimateInfo.airWeight == null) && airWeight !== estimateInfo.airWeight) ||
        (!(seaWeight === 0 && estimateInfo.seaWeight == null) && seaWeight !== estimateInfo.seaWeight) ||
        (!(storageRate === 0 && estimateInfo.storageRate == null) && storageRate !== estimateInfo.storageRate) ||
        (!(doorToDoorRate === 0 && estimateInfo.doorToDoorRate == null) && doorToDoorRate !== estimateInfo.doorToDoorRate) ||
        (!(extraTransitProtectionRate === 0 && estimateInfo.extraTransitProtectionRate == null) &&
          extraTransitProtectionRate !== estimateInfo.extraTransitProtectionRate) ||
        (!(thirdPartyEstimates === 0 && estimateInfo.thirdPartyEstimates == null) &&
          thirdPartyEstimates !== estimateInfo.thirdPartyEstimates) ||
        (!(actualDiscountRate === 0 && estimateInfo.actualDiscountRate == null) &&
          actualDiscountRate !== estimateInfo.actualDiscountRate) ||
        (!(actualStorageDiscountRate === 0 && estimateInfo.actualStorageDiscountRate == null) &&
          actualStorageDiscountRate !== estimateInfo.actualStorageDiscountRate) ||
        (!(actualLandWeight === 0 && estimateInfo.actualLandWeight == null) && actualLandWeight !== estimateInfo.actualLandWeight) ||
        (!(actualAirWeight === 0 && estimateInfo.actualAirWeight == null) && actualAirWeight !== estimateInfo.actualAirWeight) ||
        (!(actualSeaWeight === 0 && estimateInfo.actualSeaWeight == null) && actualSeaWeight !== estimateInfo.actualSeaWeight) ||
        (!(actualStorageRate === 0 && estimateInfo.actualStorageRate == null) && actualStorageRate !== estimateInfo.actualStorageRate) ||
        (!(actualDoorToDoorRate === 0 && estimateInfo.actualDoorToDoorRate == null) &&
          actualDoorToDoorRate !== estimateInfo.actualDoorToDoorRate) ||
        (!(actualExtraTransitProtectionRate === 0 && estimateInfo.actualExtraTransitProtectionRate == null) &&
          actualExtraTransitProtectionRate !== estimateInfo.actualExtraTransitProtectionRate) ||
        (!(actualThirdPartyEstimates === 0 && estimateInfo.actualThirdPartyEstimates == null) &&
          actualThirdPartyEstimates !== estimateInfo.actualThirdPartyEstimates) ||
        hasInvoicesPaid !== estimateInfo.hasInvoicesPaid
      ) {
        setIsDirty(true);
      }
    }
  }, [
    discountRate,
    storageDiscountRate,
    landWeight,
    airWeight,
    seaWeight,
    storageRate,
    doorToDoorRate,
    extraTransitProtectionRate,
    thirdPartyEstimates,
    actualDiscountRate,
    actualStorageDiscountRate,
    actualLandWeight,
    actualAirWeight,
    actualSeaWeight,
    actualStorageRate,
    actualDoorToDoorRate,
    actualExtraTransitProtectionRate,
    actualThirdPartyEstimates,
    estimateInfo,
    hasInvoicesPaid
  ]);
  
  return (
    <Dialog open={open} fullWidth={true} maxWidth={'lg'}>
      <form onSubmit={onSubmitModal} fullScreen>
        <div className="row justify-space-between align-center">
          <div className={classes.dialogIconRow}>
            <Tooltip title="Estimates" key="Estimates" placement="right">
              <FontAwesomeIcon icon={['fas', 'money-check-dollar']} size="3x" />
            </Tooltip>
            <Typography variant="h5" sx={{ paddingLeft: '20px' }}>
              Estimates
            </Typography>
          </div>
        </div>
        <div className={classes.dialogContentContainer}>
          <Stack
            sx={{
              width: '100%',
              minWidth: { xs: '300px', sm: '360px', md: '400px', lg: '400px' },
              gap: '1.5rem'
            }}
          >
            <Grid container spacing={2}>
              <Grid item md={3} />
              <Grid item md={3}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Estimates
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item md={3}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Actuals
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item md={3} />

              <EstimateInputRow
                label="Discount Rate"
                value={discountRate}
                actualValue={actualDiscountRate}
                onChange={setDiscountRate}
                onActualChange={setActualDiscountRate}
                unit="%"
              />

              <EstimateInputRow
                label="Storage Discount Rate"
                value={storageDiscountRate}
                actualValue={actualStorageDiscountRate}
                onChange={setStorageDiscountRate}
                onActualChange={setActualStorageDiscountRate}
                unit="%"
              />

              <EstimateInputRow
                label="Land Weight"
                value={landWeight}
                actualValue={actualLandWeight}
                onChange={setLandWeight}
                onActualChange={setActualLandWeight}
                unit="lb"
              />

              <EstimateInputRow
                label="Air Weight"
                value={airWeight}
                actualValue={actualAirWeight}
                onChange={setAirWeight}
                onActualChange={setActualAirWeight}
                unit="lb"
              />

              <EstimateInputRow
                label="Sea Weight"
                value={seaWeight}
                actualValue={actualSeaWeight}
                onChange={setSeaWeight}
                onActualChange={setActualSeaWeight}
                unit="lb"
              />

              <EstimateInputRow
                label="Door to Door"
                value={doorToDoorRate}
                actualValue={actualDoorToDoorRate}
                onChange={setDoorToDoorRate}
                onActualChange={setActualDoorToDoorRate}
              />

              <EstimateInputRow
                label="Storage"
                value={storageRate}
                actualValue={actualStorageRate}
                onChange={setStorageRate}
                onActualChange={setActualStorageRate}
              />

              <EstimateInputRow
                label="Extra Transit Protection"
                value={extraTransitProtectionRate}
                actualValue={actualExtraTransitProtectionRate}
                onChange={setExtraTransitProtectionRate}
                onActualChange={setActualExtraTransitProtectionRate}
              />

              <EstimateInputRow
                label="Third Party Cost"
                value={thirdPartyEstimates}
                actualValue={actualThirdPartyEstimates}
                onChange={setThirdPartyEstimates}
                onActualChange={setActualThirdPartyEstimates}
              />

              <Grid item md={3}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Total
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item md={3}>
                <Typography variant="body2">
                  {formatMoney(
                    parseFloat(doorToDoorRate || 0) +
                      parseFloat(storageRate || 0) +
                      parseFloat(extraTransitProtectionRate || 0) +
                      parseFloat(thirdPartyEstimates || 0),
                  )}
                </Typography>
              </Grid>
              <Grid item md={3}>
                <Typography variant="body2">
                  {formatMoney(
                    parseFloat(actualDoorToDoorRate || 0) +
                      parseFloat(actualStorageRate || 0) +
                      parseFloat(actualExtraTransitProtectionRate || 0) +
                      parseFloat(actualThirdPartyEstimates || 0),
                  )}
                </Typography>
              </Grid>
              <Grid item md={3}></Grid>
              <Grid item md={3}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Invoices Paid
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item md={3}>
                <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem', marginTop: '-9px' }}>
                  <Checkbox checked={hasInvoicesPaid} onChange={e => setHasInvoicesPaid(e.target.checked)} />
                </Typography>
              </Grid>

              <Grid item md={3} />
              <Grid item md={3} />
            </Grid>
          </Stack>
        </div>
        {isLoading && (
          <DialogActions sx={{ p: '1.25rem' }}>
            <Spinner />
          </DialogActions>
        )}
        {!isLoading && (
          <DialogActions sx={{ p: '1.25rem' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button color="primary" type="submit" variant="contained" disabled={!isDirty}>
              Save
            </Button>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
};

const GoodsEstimatesCard = props => {
  const [isLoading, setIsLoading] = useState(false);
  const [editingModalOpen, setEditingModalOpen] = useState(false);

  const classes = useStyles();

  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });

  const { estimateInfo } = props.estimate;

  const updateFields = async values => {
    setIsLoading(true);

    let newVals = { ...estimateInfo };
    newVals.discountRate = values.discountRate;
    newVals.storageDiscountRate = values.storageDiscountRate;
    newVals.landWeight = values.landWeight;
    newVals.airWeight = values.airWeight;
    newVals.seaWeight = values.seaWeight;
    newVals.doorToDoorRate = values.doorToDoorRate;
    newVals.storageRate = values.storageRate;
    newVals.extraTransitProtectionRate = values.extraTransitProtectionRate;
    newVals.thirdPartyEstimates = values.thirdPartyEstimates;

    newVals.actualDiscountRate = values.actualDiscountRate;
    newVals.actualStorageDiscountRate = values.actualStorageDiscountRate;
    newVals.actualLandWeight = values.actualLandWeight;
    newVals.actualAirWeight = values.actualAirWeight;
    newVals.actualSeaWeight = values.actualSeaWeight;
    newVals.actualStorageRate = values.actualStorageRate;
    newVals.actualDoorToDoorRate = values.actualDoorToDoorRate;
    newVals.actualExtraTransitProtectionRate = values.actualExtraTransitProtectionRate;
    newVals.actualThirdPartyEstimates = values.actualThirdPartyEstimates;

    newVals.hasInvoicesPaid = values.hasInvoicesPaid;

    let newRequestBody = { ...props.estimate };
    newRequestBody.estimateInfo = newVals;

    const resp = await props.updateHhgEstimate(props.authId, newRequestBody);
    if (resp.type === UPDATE_HHG_ESTIMATE_FAILURE) {
      props.showToast('Failed to update the estimate info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoading(false);
      return false;
    } else {
      await props.getHhgSegments(props.authId);
      setIsLoading(false);
      return true;
    }
  };

  const handleSaveEdits = async values => {
    // Send update call to parent to update and refresh values.
    const resp = await updateFields(values);
    if (!resp) {
      showToast('Failed to update card data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }
    setEditingModalOpen(false); //required to exit editing mode and close modal
  };

  useEffect(() => {}, [estimateInfo]);

  return (
    <ThemeProvider theme={theme}>
      <Card className={classes.card}>
        <Box>
          <CardContent className={classes.cardContainer}>
            <div className="row justify-space-between align-center">
              <div className={classes.iconRow}>
                <Tooltip title="Estimates" key="Estimates" placement="right">
                  <FontAwesomeIcon icon={['fas', 'money-check-dollar']} size="2x" />
                </Tooltip>
                <Typography variant="h5" sx={{ paddingLeft: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
                  Estimates
                </Typography>
              </div>
              {!props.hideEdit && (
                <>
                  <Fab className={classes.iconOverlayImg} color="primary" size="small" onClick={() => setEditingModalOpen(true)}>
                    <FontAwesomeIcon size="1x" icon={['fas', 'pen']} color={theme.palette.common.white} />
                  </Fab>
                </>
              )}
            </div>
          </CardContent>
          <CardContent sx={{ padding: '10px', marginLeft: '30px' }}>
            <Grid container spacing={2}>
              <Grid item md={3} />
              <Grid item md={3}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Estimates
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item md={3}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Actuals
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item md={3} />
              <EstimateDisplayRow
                label="Discount Rate"
                value={`${estimateInfo?.discountRate ?? 0}%`}
                actualValue={`${estimateInfo?.actualDiscountRate ?? 0}%`}
              />

              <EstimateDisplayRow
                label="Storage Discount Rate"
                value={`${estimateInfo?.storageDiscountRate ?? 0}%`}
                actualValue={`${estimateInfo?.actualStorageDiscountRate ?? 0}%`}
              />

              <EstimateDisplayRow
                label="Land Weight"
                value={`${estimateInfo?.landWeight ?? 0} lb`}
                actualValue={`${estimateInfo?.actualLandWeight ?? 0} lb`}
              />

              <EstimateDisplayRow
                label="Air Weight"
                value={`${estimateInfo?.airWeight ?? 0} lb`}
                actualValue={`${estimateInfo?.actualAirWeight ?? 0} lb`}
              />

              <EstimateDisplayRow
                label="Sea Weight"
                value={`${estimateInfo?.seaWeight ?? 0} lb`}
                actualValue={`${estimateInfo?.actualSeaWeight ?? 0} lb`}
              />

              <EstimateDisplayRow
                label="Door to Door"
                value={formatMoney(estimateInfo?.doorToDoorRate ?? 0)}
                actualValue={formatMoney(estimateInfo?.actualDoorToDoorRate ?? 0)}
              />

              <EstimateDisplayRow
                label="Storage"
                value={formatMoney(estimateInfo?.storageRate ?? 0)}
                actualValue={formatMoney(estimateInfo?.actualStorageRate ?? 0)}
              />

              <EstimateDisplayRow
                label="Extra Transit Protection"
                value={formatMoney(estimateInfo?.extraTransitProtectionRate ?? 0)}
                actualValue={formatMoney(estimateInfo?.actualExtraTransitProtectionRate ?? 0)}
              />

              <EstimateDisplayRow
                label="Third Party Cost"
                value={formatMoney(estimateInfo?.thirdPartyEstimates ?? 0)}
                actualValue={formatMoney(estimateInfo?.actualThirdPartyEstimates ?? 0)}
              />

              <EstimateDisplayRow
                label="Total"
                value={formatMoney(
                  parseFloat(estimateInfo?.doorToDoorRate || 0) +
                    parseFloat(estimateInfo?.storageRate || 0) +
                    parseFloat(estimateInfo?.extraTransitProtectionRate || 0) +
                    parseFloat(estimateInfo?.thirdPartyEstimates || 0),
                )}
                actualValue={formatMoney(
                  parseFloat(estimateInfo?.actualDoorToDoorRate || 0) +
                    parseFloat(estimateInfo?.actualStorageRate || 0) +
                    parseFloat(estimateInfo?.actualExtraTransitProtectionRate || 0) +
                    parseFloat(estimateInfo?.actualThirdPartyEstimates || 0),
                )}
              />

              <Grid item md={3}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Invoices Paid
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item md={3}>
                <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem', marginTop: '-9px' }}>
                  <Checkbox checked={Boolean(estimateInfo?.hasInvoicesPaid)} disabled />
                </Typography>
              </Grid>

              <Grid item md={3} />
              <Grid item md={3} />
            </Grid>
          </CardContent>
        </Box>
      </Card>
      {editingModalOpen && (
        <EditCardModal
          classes={classes}
          tab={0}
          open={editingModalOpen}
          isLoading={isLoading}
          onClose={() => setEditingModalOpen(false)}
          onSubmit={handleSaveEdits}
          estimateInfo={estimateInfo}
          itemsTotal={0}
        />
      )}
    </ThemeProvider>
  );
};

export default compose(
  connect(null, {
    updateHhgEstimate,
    showToast,
    getHhgSegments
  })
)(GoodsEstimatesCard);
